/**
 * Explicit PCS permission keys — mirrors Vidhyantra_Backend rbac/catalog/submenu-feature-registry.ts.
 * Domain slugs are concatenated [a-z0-9] only; never route-path derived.
 */

export type PermissionScope = 'platform' | 'organization' | 'branch'
export type PermissionAction = 'view' | 'create' | 'edit' | 'delete'

export function buildFeatureKey(scope: PermissionScope, domain: string): string {
  return `${scope}.${domain}`
}

export function buildPermissionKey(
  scope: PermissionScope,
  domain: string,
  action: PermissionAction
): string {
  return `${scope}.${domain}.${action}`
}

/** Registry routes → explicit domain (branch scope). */
const BRANCH_ROUTE_DOMAIN: Readonly<Record<string, string>> = {
  '/branch/academics/calendar': 'academiccalendar',
  '/branch/academics/setup': 'academicsetup',
  '/branch/academics/timetable': 'timetable',
  '/branch/admissions/forms': 'admissionforms',
  '/branch/admissions/payments': 'admissionpayments',
  '/branch/admissions/pipeline': 'admissionpipeline',
  '/branch/admissions/overview': 'admissionsoverview',
  '/branch/reports/analytics': 'analytics',
  '/branch/announcements': 'announcements',
  '/branch/admissions': 'applications',
  '/branch/attendanceAnalytics': 'attendanceanalytics',
  '/branch/student/attendance/history': 'attendancehistory',
  '/branch/settings/audit': 'auditviewer',
  '/branch/settings/general': 'branchsettings',
  '/branch/services/campaigns': 'campaigns',
  '/branch/classManagement': 'classmanagement',
  '/branch/admissions/progression': 'classprogression',
  '/branch/dashboard': 'dashboard',
  '/branch/academics/diary': 'diary',
  '/branch/hr/onboarding': 'employeeonboarding',
  '/branch/admissions/enrollment': 'enrollment',
  '/branch/examAnalytics': 'examanalytics',
  '/branch/examinations/exam-management': 'exammanagement',
  '/branch/expenseManagement': 'expenses',
  '/branch/feeManagement': 'feecollection',
  '/branch/feestructures': 'feestructure',
  '/branch/finance': 'feesummary',
  '/branch/finance/setup': 'financesetup',
  '/branch/academics/homework': 'homework',
  '/branch/academics/homework/manage': 'homeworkmanagement',
  '/branch/academics/homework/analytics': 'homeworkmanagement',
  '/branch/hostels': 'hostel',
  '/branch/inventory': 'inventory',
  '/branch/invoiceManagement': 'invoices',
  '/branch/student/learning-resources': 'lessonplanning',
  '/branch/leaveManagement': 'leavemanagement',
  '/branch/attendance/leave-management': 'leavemanagement',
  '/branch/attendance/my-leaves': 'myleaves',
  '/branch/attendance/my-attendance': 'myattendance',
  '/branch/lessonPlanning': 'lessonplanning',
  '/branch/bookManagement': 'library',
  '/branch/marksEntry': 'marksentry',
  '/branch/teacher/exams': 'teacherexams',
  '/branch/feedbackCollaboration': 'messages',
  '/branch/workspace/my-attendance': 'myattendance',
  '/branch/teacher/classes': 'myclasses',
  '/branch/my-workspace/documents': 'mydocuments',
  '/branch/teacher/my-leaves': 'myleaves',
  '/branch/settings/preferences': 'mypreferences',
  '/branch/workspace/my-profile': 'myprofile',
  '/branch/teacher/timetable': 'myschedule',
  '/branch/teacher/students': 'mystudents',
  '/branch/student/subjects': 'mysubjects',
  '/branch/settings/notifications': 'mypreferences',
  '/branch/notifications': 'notifications',
  '/branch/services/parentCommunicationPortal': 'parentcommunication',
  '/branch/payments/operations': 'paymentoperations',
  '/branch/payments': 'payments',
  '/branch/payroll': 'payroll',
  '/branch/workspace/payslips': 'payslips',
  '/branch/announcements/manage': 'postannouncements',
  '/branch/promotions': 'promotions',
  '/branch/refundManagement': 'refunds',
  '/branch/reports': 'reportscenter',
  '/branch/results': 'results',
  '/branch/revenueAnalytics': 'revenueanalytics',
  '/branch/settings/access': 'rolesaccess',
  '/branch/reports/saved': 'savedreports',
  '/branch/reports/scheduled': 'scheduledreports',
  '/branch/staffAttendance': 'staffattendance',
  '/branch/staffDirectory': 'staffdirectory',
  '/branch/studentAttendance': 'studentattendance',
  '/branch/reports/studentReports': 'studentreports',
  '/branch/vehiclesDrivers': 'transport',
}

/** Detail / alias routes → parent submenu domain (longest prefix wins). */
const BRANCH_ROUTE_PREFIX_DOMAIN: ReadonlyArray<readonly [string, string]> = [
  ['/branch/academics/calendar', 'academiccalendar'],
  ['/branch/academics/setup', 'academicsetup'],
  ['/branch/examinations/exam-management', 'exammanagement'],
  ['/branch/finance/setup', 'financesetup'],
  ['/branch/examManagement', 'exammanagement'],
  ['/branch/feesetup', 'feestructure'],
  ['/branch/hr/onboarding', 'employeeonboarding'],
  ['/branch/employee/onboarding', 'employeeonboarding'],
  ['/branch/employees', 'staffdirectory'],
  ['/branch/admissions', 'applications'],
  ['/branch/students', 'classmanagement'],
  ['/branch/teacher/marks-entry', 'marksentry'],
  ['/branch/teacher/student-profiles', 'mystudents'],
  ['/branch/teacher/performance-overview', 'mystudents'],
  ['/branch/teacher/exams', 'teacherexams'],
  ['/branch/teacher/live-class', 'myclasses'],
  ['/branch/lessonPlanning', 'lessonplanning'],
  ['/branch/student/timetable', 'myschedule'],
  ['/branch/student/attendance', 'myattendance'],
  ['/branch/attendance/my-attendance', 'myattendance'],
  ['/branch/attendance/my-leaves', 'myleaves'],
  ['/branch/attendance/leave-management', 'leavemanagement'],
  ['/branch/parent/child-attendance', 'parentcommunication'],
  ['/branch/roles/rolesmanagement', 'rolesaccess'],
  ['/branch/group/groupmanagement', 'rolesaccess'],
  ['/branch/attendance', 'studentattendance'],
]

function normalizeScopeRoute(route: string): string {
  return route
    .trim()
    .replace(/\/:[^/]+/g, '')
    .replace(/\/\*+/g, '')
    .replace(/\/$/, '')
}

/** Registry routes → explicit domain (platform scope). Mirrors BE submenu-feature-registry. */
const PLATFORM_ROUTE_DOMAIN: Readonly<Record<string, string>> = {
  '/platform/dashboard': 'dashboard',
  '/platform/organizations/view-all': 'organizationmanagement',
  '/platform/organizations/create': 'createorganization',
  '/platform/organizations/branches': 'branchmanagement',
  '/platform/organizations/setup-templates': 'setuptemplates',
  '/platform/billing/subscriptions': 'subscriptions',
  '/platform/billing/subscription-plans': 'subscriptionplans',
  '/platform/billing/invoices': 'invoicemanagement',
  '/platform/billing/payment-logs': 'paymentlogs',
  '/platform/billing/revenue-analytics': 'revenueanalytics',
  '/platform/billing/pricing': 'platformpriceconfig',
  '/platform/billing/pilots': 'platformsettings',
  '/platform/billing/organizations': 'organizationmanagement',
  '/platform/configuration/coupons': 'couponmanagement',
  '/platform/configuration/feature-catalog': 'featurecatalog',
  '/platform/configuration/feature-flags': 'featureflags',
  '/platform/configuration/menus': 'menumanagement',
  '/platform/configuration/dashboard-widgets': 'dashboardwidgets',
  '/platform/jobs': 'jobs',
  '/platform/configuration/form-management': 'formmanagement',
  '/platform/configuration/document-templates': 'documenttemplates',
  '/platform/configuration/carousels': 'contentcarousels',
  '/platform/access/users': 'platformusers',
  '/platform/access/group-users': 'groupusers',
  '/platform/configuration/roles': 'rolemanagement',
  '/platform/configuration/groups': 'groupmanagement',
  '/platform/auditlogs/view': 'audit',
  '/platform/webhook-events/logs': 'webhooklogs',
  '/platform/security/authentication-policies': 'authenticationpolicies',
  '/platform/security/secrets': 'secretmanagement',
  '/platform/security/webhooks': 'webhooks',
  '/platform/analytics/overview': 'platformoverview',
  '/platform/analytics/metrics': 'platformmetrics',
  '/platform/analytics/usage': 'usageanalytics',
  '/platform/analytics/system-health': 'systemhealth',
  '/platform/system-health': 'systemhealth',
  '/platform/analytics/user-growth': 'usergrowth',
  '/platform/support/tools': 'supporttools',
  '/platform/support/services': 'supportservices',
  '/platform/suggestions/manage': 'suggestions',
  '/platform/support/platform-logs': 'platformlogs',
  '/platform/app-settings/manage-settings': 'platformsettings',
  '/platform/app-settings/themes': 'themes',
  '/platform/app-settings/font-sizes': 'fontsizes',
  '/platform/app-settings/settings': 'managesettings',
  '/platform/design-library': 'documenttemplates',
}

/** Detail / alias routes → parent submenu domain (longest prefix wins). */
const PLATFORM_ROUTE_PREFIX_DOMAIN: ReadonlyArray<readonly [string, string]> = [
  ['/platform/rolemanagement/roles', 'rolemanagement'],
  ['/platform/configuration/roles', 'rolemanagement'],
  ['/platform/configuration/groups', 'groupmanagement'],
  ['/platform/configuration/menus', 'menumanagement'],
  ['/platform/configuration/carousels', 'contentcarousels'],
  ['/platform/configuration/coupons', 'couponmanagement'],
  ['/platform/configuration/form-management', 'formmanagement'],
  ['/platform/configuration/document-templates', 'documenttemplates'],
  ['/platform/configuration/feature-catalog', 'featurecatalog'],
  ['/platform/billing/subscription-plans', 'subscriptionplans'],
  ['/platform/app-settings/themes', 'themes'],
  ['/platform/app-settings/font-sizes', 'fontsizes'],
  ['/platform/app-settings/settings', 'managesettings'],
  ['/platform/app-settings/manage-settings', 'platformsettings'],
  ['/platform/support/services', 'supportservices'],
  ['/platform/jobs', 'jobs'],
]

/** Registry routes → explicit domain (organization scope). Mirrors BE submenu-feature-registry. */
const ORGANIZATION_ROUTE_DOMAIN: Readonly<Record<string, string>> = {
  '/org/dashboard': 'dashboard',
  '/org/branches/manage': 'branches',
  '/org/branches/configuration': 'branchconfiguration',
  '/org/branches/forms': 'forms',
  '/org/users/roles-permissions': 'roles',
  '/org/users/groups': 'roles',
  '/org/access/approval-policies': 'roles',
  '/org/access/workflow-automation': 'workflowautomation',
  '/org/access/custom-roles': 'roles',
  '/org/billing': 'billingoverview',
  '/org/billing/subscription': 'subscription',
  '/org/billing/invoices': 'invoices',
  '/org/billing/payments': 'payments',
  '/org/billing/wallet': 'wallet',
  '/org/billing/ledger': 'ledger',
  '/org/billing/finance-settings': 'financesettings',
  '/org/app-settings/advanced': 'organizationsettings',
  '/org/app-settings/profile': 'organizationprofile',
  '/org/app-settings/custom-branding': 'custombranding',
  '/org/app-settings/training-tutorials': 'trainingtutorials',
  '/org/analytics/reports': 'organizationreports',
  '/org/advanced/data-export': 'dataexport',
  '/org/analytics/financial-reports': 'financialreports',
  '/org/analytics/compliance-monitoring': 'audit',
}

/** Detail / alias routes → parent submenu domain (longest prefix wins). */
const ORGANIZATION_ROUTE_PREFIX_DOMAIN: ReadonlyArray<readonly [string, string]> = [
  ['/org/branches/forms', 'forms'],
  ['/org/branches/manage', 'branches'],
  ['/org/branches/configuration', 'branchconfiguration'],
  ['/org/access/approval-policies', 'roles'],
  ['/org/access/workflow-automation', 'workflowautomation'],
  ['/org/app-settings/advanced', 'organizationsettings'],
  ['/org/app-settings/profile', 'organizationprofile'],
  ['/org/app-settings/custom-branding', 'custombranding'],
  ['/org/app-settings/training-tutorials', 'trainingtutorials'],
  ['/org/billing/finance-settings', 'financesettings'],
  ['/org/billing/ledger', 'ledger'],
  ['/org/billing/invoices', 'invoices'],
  ['/org/billing/subscription', 'subscription'],
  ['/org/billing/payments', 'payments'],
  ['/org/billing/wallet', 'wallet'],
  ['/org/billing', 'billingoverview'],
]

export function routeToFeatureDomain(scope: PermissionScope, route: string): string {
  const normalized = normalizeScopeRoute(route)

  if (scope === 'branch') {
    const exact = BRANCH_ROUTE_DOMAIN[normalized]
    if (exact) return exact

    for (const [prefix, domain] of BRANCH_ROUTE_PREFIX_DOMAIN) {
      if (normalized === prefix || normalized.startsWith(`${prefix}/`)) {
        return domain
      }
    }

    throw new Error(`[PCS] No explicit domain for branch route ${route}`)
  }

  if (scope === 'platform') {
    const exact = PLATFORM_ROUTE_DOMAIN[normalized]
    if (exact) return exact

    for (const [prefix, domain] of PLATFORM_ROUTE_PREFIX_DOMAIN) {
      if (normalized === prefix || normalized.startsWith(`${prefix}/`)) {
        return domain
      }
    }

    throw new Error(`[PCS] No explicit domain for platform route ${route}`)
  }

  if (scope === 'organization') {
    const exact = ORGANIZATION_ROUTE_DOMAIN[normalized]
    if (exact) return exact

    for (const [prefix, domain] of ORGANIZATION_ROUTE_PREFIX_DOMAIN) {
      if (normalized === prefix || normalized.startsWith(`${prefix}/`)) {
        return domain
      }
    }

    throw new Error(`[PCS] No explicit domain for organization route ${route}`)
  }

  throw new Error(`routeToFeatureDomain: explicit registry not defined for scope ${scope} (${route})`)
}

/** Primary menu-gate permission for a submenu route (defaults to view). */
export function menuPermissionForRoute(
  scope: PermissionScope,
  route: string,
  action: PermissionAction = 'view'
): string {
  const domain = routeToFeatureDomain(scope, route)
  return buildPermissionKey(scope, domain, action)
}

/** Branch settings panel routes (SubMenuTabBar). */
export const BRANCH_SETTINGS_PERMISSIONS = {
  academicSetup: buildPermissionKey('branch', 'academicsetup', 'view'),
  timetable: buildPermissionKey('branch', 'timetable', 'view'),
  academicCalendar: buildPermissionKey('branch', 'academiccalendar', 'view'),
  examManagement: buildPermissionKey('branch', 'exammanagement', 'view'),
  rolesAccess: buildPermissionKey('branch', 'rolesaccess', 'view'),
  general: buildPermissionKey('branch', 'branchsettings', 'view'),
  audit: buildPermissionKey('branch', 'auditviewer', 'view'),
  preferences: buildPermissionKey('branch', 'mypreferences', 'view'),
  finance: buildPermissionKey('branch', 'financesetup', 'view'),
  notifications: buildPermissionKey('branch', 'mypreferences', 'view'),
} as const

/** Branch calendar API + settings academic calendar panel. */
export const BRANCH_ACADEMIC_CALENDAR_PERMISSIONS = {
  view: buildPermissionKey('branch', 'academiccalendar', 'view'),
  create: buildPermissionKey('branch', 'academiccalendar', 'create'),
  edit: buildPermissionKey('branch', 'academiccalendar', 'edit'),
  delete: buildPermissionKey('branch', 'academiccalendar', 'delete'),
} as const

/** @deprecated Use {@link BRANCH_ACADEMIC_CALENDAR_PERMISSIONS} — events live under academic calendar. */
export const BRANCH_EVENTS_PERMISSIONS = BRANCH_ACADEMIC_CALENDAR_PERMISSIONS

/** Branch attendance submenu routes. */
export const BRANCH_ATTENDANCE_PERMISSIONS = {
  student: buildPermissionKey('branch', 'studentattendance', 'create'),
  staff: buildPermissionKey('branch', 'staffattendance', 'view'),
  analytics: buildPermissionKey('branch', 'attendanceanalytics', 'view'),
  history: buildPermissionKey('branch', 'attendancehistory', 'view'),
  my: buildPermissionKey('branch', 'myattendance', 'view'),
  myLeaves: buildPermissionKey('branch', 'myleaves', 'view'),
  leaveManagement: buildPermissionKey('branch', 'leavemanagement', 'view'),
  parentCommunication: buildPermissionKey('branch', 'parentcommunication', 'view'),
} as const

/** Supplemental API domains (no dedicated submenu). */
export const BRANCH_SUPPLEMENTAL_PERMISSIONS = {
  usersView: buildPermissionKey('branch', 'users', 'view'),
  usersCreate: buildPermissionKey('branch', 'users', 'create'),
  usersEdit: buildPermissionKey('branch', 'users', 'edit'),
  employeeView: buildPermissionKey('branch', 'employee', 'view'),
  employeeCreate: buildPermissionKey('branch', 'employee', 'create'),
  employeeEdit: buildPermissionKey('branch', 'employee', 'edit'),
  staffDirectoryEdit: buildPermissionKey('branch', 'staffdirectory', 'edit'),
  formsView: buildPermissionKey('branch', 'forms', 'view'),
  formsCreate: buildPermissionKey('branch', 'forms', 'create'),
  formsEdit: buildPermissionKey('branch', 'forms', 'edit'),
} as const

/** Attendance lock bypass — any attendance submenu edit grant is sufficient. */
export const ATTENDANCE_OVERRIDE_PERMISSIONS = [
  buildPermissionKey('branch', 'studentattendance', 'edit'),
  buildPermissionKey('branch', 'staffattendance', 'edit'),
  buildPermissionKey('branch', 'attendanceanalytics', 'edit'),
] as const

export function hasAttendanceOverridePermission(permissions: readonly string[] | undefined): boolean {
  if (!permissions?.length) return false
  return ATTENDANCE_OVERRIDE_PERMISSIONS.some((key) => permissions.includes(key))
}

/** CRUD keys for the Academic setup panel (`/branch/academics/setup`). */
export const BRANCH_ACADEMIC_SETUP_PERMISSIONS = {
  view: buildPermissionKey('branch', 'academicsetup', 'view'),
  create: buildPermissionKey('branch', 'academicsetup', 'create'),
  edit: buildPermissionKey('branch', 'academicsetup', 'edit'),
  delete: buildPermissionKey('branch', 'academicsetup', 'delete'),
} as const

/** CRUD keys for a branch settings panel route (view from {@link BRANCH_SETTINGS_PERMISSIONS}). */
export function branchSettingsPanelAction(
  panel: keyof typeof BRANCH_SETTINGS_PERMISSIONS,
  action: PermissionAction
): string {
  const viewKey = BRANCH_SETTINGS_PERMISSIONS[panel]
  const domain = viewKey.split('.')[1] ?? ''
  return buildPermissionKey('branch', domain, action)
}

/** Platform app-settings panel routes (SubMenuTabBar). */
export const PLATFORM_SETTINGS_PERMISSIONS = {
  manageSettings: buildPermissionKey('platform', 'platformsettings', 'view'),
  themes: buildPermissionKey('platform', 'themes', 'view'),
  fontSizes: buildPermissionKey('platform', 'fontsizes', 'view'),
  settingsDefinitions: buildPermissionKey('platform', 'managesettings', 'view'),
  featureCatalog: buildPermissionKey('platform', 'featurecatalog', 'view'),
  subscriptionPlans: buildPermissionKey('platform', 'subscriptionplans', 'view'),
} as const

/** CRUD keys for a platform app-settings panel route. */
export function platformSettingsPanelAction(
  panel: keyof typeof PLATFORM_SETTINGS_PERMISSIONS,
  action: PermissionAction
): string {
  const viewKey = PLATFORM_SETTINGS_PERMISSIONS[panel]
  const domain = viewKey.split('.')[1] ?? ''
  return buildPermissionKey('platform', domain, action)
}

/** Supplemental platform API domains (no dedicated submenu). */
export const PLATFORM_SUPPLEMENTAL_PERMISSIONS = {
  usersView: buildPermissionKey('platform', 'users', 'view'),
  usersCreate: buildPermissionKey('platform', 'users', 'create'),
  usersEdit: buildPermissionKey('platform', 'users', 'edit'),
  usersDelete: buildPermissionKey('platform', 'users', 'delete'),
  formsView: buildPermissionKey('platform', 'forms', 'view'),
  formsCreate: buildPermissionKey('platform', 'forms', 'create'),
  formsEdit: buildPermissionKey('platform', 'forms', 'edit'),
  formsDelete: buildPermissionKey('platform', 'forms', 'delete'),
} as const

export const PLATFORM_ORGANIZATIONMANAGEMENT_PERMISSIONS = {
  view: buildPermissionKey('platform', 'organizationmanagement', 'view'),
  create: buildPermissionKey('platform', 'organizationmanagement', 'create'),
  edit: buildPermissionKey('platform', 'organizationmanagement', 'edit'),
  delete: buildPermissionKey('platform', 'organizationmanagement', 'delete'),
} as const

export const PLATFORM_ROLEMANAGEMENT_PERMISSIONS = {
  view: buildPermissionKey('platform', 'rolemanagement', 'view'),
  create: buildPermissionKey('platform', 'rolemanagement', 'create'),
  edit: buildPermissionKey('platform', 'rolemanagement', 'edit'),
  delete: buildPermissionKey('platform', 'rolemanagement', 'delete'),
} as const

export const PLATFORM_GROUPMANAGEMENT_PERMISSIONS = {
  view: buildPermissionKey('platform', 'groupmanagement', 'view'),
  create: buildPermissionKey('platform', 'groupmanagement', 'create'),
  edit: buildPermissionKey('platform', 'groupmanagement', 'edit'),
  delete: buildPermissionKey('platform', 'groupmanagement', 'delete'),
} as const

export const PLATFORM_CONTENTCAROUSELS_PERMISSIONS = {
  view: buildPermissionKey('platform', 'contentcarousels', 'view'),
  create: buildPermissionKey('platform', 'contentcarousels', 'create'),
  edit: buildPermissionKey('platform', 'contentcarousels', 'edit'),
  delete: buildPermissionKey('platform', 'contentcarousels', 'delete'),
} as const

export const PLATFORM_DASHBOARDWIDGETS_PERMISSIONS = {
  view: buildPermissionKey('platform', 'dashboardwidgets', 'view'),
  create: buildPermissionKey('platform', 'dashboardwidgets', 'create'),
  edit: buildPermissionKey('platform', 'dashboardwidgets', 'edit'),
  delete: buildPermissionKey('platform', 'dashboardwidgets', 'delete'),
} as const

export const PLATFORM_FEATUREFLAGS_PERMISSIONS = {
  view: buildPermissionKey('platform', 'featureflags', 'view'),
  create: buildPermissionKey('platform', 'featureflags', 'create'),
  edit: buildPermissionKey('platform', 'featureflags', 'edit'),
  delete: buildPermissionKey('platform', 'featureflags', 'delete'),
} as const

export const PLATFORM_MENUMANAGEMENT_PERMISSIONS = {
  view: buildPermissionKey('platform', 'menumanagement', 'view'),
  create: buildPermissionKey('platform', 'menumanagement', 'create'),
  edit: buildPermissionKey('platform', 'menumanagement', 'edit'),
  delete: buildPermissionKey('platform', 'menumanagement', 'delete'),
} as const

export const PLATFORM_FORMMANAGEMENT_PERMISSIONS = {
  view: buildPermissionKey('platform', 'formmanagement', 'view'),
  create: buildPermissionKey('platform', 'formmanagement', 'create'),
  edit: buildPermissionKey('platform', 'formmanagement', 'edit'),
  delete: buildPermissionKey('platform', 'formmanagement', 'delete'),
} as const

export const PLATFORM_DOCUMENTTEMPLATES_PERMISSIONS = {
  view: buildPermissionKey('platform', 'documenttemplates', 'view'),
  create: buildPermissionKey('platform', 'documenttemplates', 'create'),
  edit: buildPermissionKey('platform', 'documenttemplates', 'edit'),
  delete: buildPermissionKey('platform', 'documenttemplates', 'delete'),
} as const

export const PLATFORM_COUPONMANAGEMENT_PERMISSIONS = {
  view: buildPermissionKey('platform', 'couponmanagement', 'view'),
  create: buildPermissionKey('platform', 'couponmanagement', 'create'),
  edit: buildPermissionKey('platform', 'couponmanagement', 'edit'),
  delete: buildPermissionKey('platform', 'couponmanagement', 'delete'),
} as const

export const PLATFORM_SUBSCRIPTIONPLANS_PERMISSIONS = {
  view: buildPermissionKey('platform', 'subscriptionplans', 'view'),
  create: buildPermissionKey('platform', 'subscriptionplans', 'create'),
  edit: buildPermissionKey('platform', 'subscriptionplans', 'edit'),
  delete: buildPermissionKey('platform', 'subscriptionplans', 'delete'),
} as const

export const ORGANIZATION_FORMS_PERMISSIONS = {
  view: buildPermissionKey('organization', 'forms', 'view'),
  create: buildPermissionKey('organization', 'forms', 'create'),
  edit: buildPermissionKey('organization', 'forms', 'edit'),
  delete: buildPermissionKey('organization', 'forms', 'delete'),
} as const

export const ORGANIZATION_BRANCHES_PERMISSIONS = {
  view: buildPermissionKey('organization', 'branches', 'view'),
  create: buildPermissionKey('organization', 'branches', 'create'),
  edit: buildPermissionKey('organization', 'branches', 'edit'),
  delete: buildPermissionKey('organization', 'branches', 'delete'),
} as const

export const ORGANIZATION_BRANCHCONFIGURATION_PERMISSIONS = {
  view: buildPermissionKey('organization', 'branchconfiguration', 'view'),
  create: buildPermissionKey('organization', 'branchconfiguration', 'create'),
  edit: buildPermissionKey('organization', 'branchconfiguration', 'edit'),
  delete: buildPermissionKey('organization', 'branchconfiguration', 'delete'),
} as const

export const ORGANIZATION_BILLING_PERMISSIONS = {
  overview: buildPermissionKey('organization', 'billingoverview', 'view'),
  subscription: buildPermissionKey('organization', 'subscription', 'view'),
  invoices: buildPermissionKey('organization', 'invoices', 'view'),
  payments: buildPermissionKey('organization', 'payments', 'view'),
  wallet: buildPermissionKey('organization', 'wallet', 'view'),
  ledger: buildPermissionKey('organization', 'ledger', 'view'),
  ledgerEdit: buildPermissionKey('organization', 'ledger', 'edit'),
} as const

export const ORGANIZATION_ROLES_PERMISSIONS = {
  view: buildPermissionKey('organization', 'roles', 'view'),
  create: buildPermissionKey('organization', 'roles', 'create'),
  edit: buildPermissionKey('organization', 'roles', 'edit'),
  delete: buildPermissionKey('organization', 'roles', 'delete'),
} as const

export const ORGANIZATION_APP_SETTINGS_PERMISSIONS = {
  advanced: buildPermissionKey('organization', 'organizationsettings', 'view'),
  profile: buildPermissionKey('organization', 'organizationprofile', 'view'),
  customBranding: buildPermissionKey('organization', 'custombranding', 'edit'),
  trainingTutorials: buildPermissionKey('organization', 'trainingtutorials', 'view'),
  financeSettings: buildPermissionKey('organization', 'financesettings', 'view'),
  workflowAutomation: buildPermissionKey('organization', 'workflowautomation', 'edit'),
} as const

/** @deprecated Use branchSettingsPanelAction */
export const settingsPanelAction = branchSettingsPanelAction
